import React from 'react';

/**
 * Mini-cartouche flottant qui apparaît au-dessus d'un calque sélectionné
 * pour permettre l'édition précise de ses dimensions (largeur, hauteur,
 * rotation) avec boutons +/- comme dans l'inspecteur.
 *
 * - Apparait sur les calques rotates (les handles re-resizable sont alors
 *   desactives car elles ne suivent pas la rotation visuelle).
 * - Deplacable via une poignée à gauche pour ne pas gêner l'édition.
 * - Rendu HORS du wrapper data-rotated-inner pour ne pas etre rotate.
 *
 * @param {object} props
 * @param {object} props.el - Calque sélectionné
 * @param {object} props.canvas - State canvas (pour scale)
 * @param {function} props.updateElement - Action store pour MAJ le calque
 */
export default function DimensionsHUD({ el, canvas, updateElement }) {
    const scale = canvas.scale || 1;

    const [dragOffset, setDragOffset] = React.useState({ x: 0, y: 0 });
    const dragStateRef = React.useRef(null);

    React.useEffect(() => {
        setDragOffset({ x: 0, y: 0 });
    }, [el.id]);

    React.useEffect(() => {
        const onMove = (e) => {
            if (!dragStateRef.current) return;
            const { startX, startY, baseX, baseY } = dragStateRef.current;
            setDragOffset({
                x: baseX + (e.clientX - startX) / scale,
                y: baseY + (e.clientY - startY) / scale,
            });
        };
        const onUp = () => {
            dragStateRef.current = null;
            document.body.style.cursor = '';
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [scale]);

    const startDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragStateRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            baseX: dragOffset.x,
            baseY: dragOffset.y,
        };
        document.body.style.cursor = 'grabbing';
    };

    const hudStyle = {
        position: 'absolute',
        top: `${-40 / scale + dragOffset.y}px`,
        left: `calc(50% + ${dragOffset.x}px)`,
        transform: `translateX(-50%) scale(${1 / scale})`,
        transformOrigin: 'top center',
        zIndex: 60,
        pointerEvents: 'auto',
    };

    const updateW = (newW) => {
        const w = Math.max(5, Math.round(Number(newW) * 10) / 10);
        if (!isNaN(w)) updateElement(el.id, { width: w });
    };
    const updateH = (newH) => {
        const h = Math.max(5, Math.round(Number(newH) * 10) / 10);
        if (!isNaN(h)) updateElement(el.id, { height: h });
    };
    const updateRotation = (newRot) => {
        let r = Number(newRot);
        if (isNaN(r)) return;
        r = ((r % 360) + 360) % 360;
        updateElement(el.id, { rotation: Math.round(r * 10) / 10 });
    };

    const widthRounded = Math.round(el.width * 10) / 10;
    const heightRounded = Math.round(el.height * 10) / 10;
    const rotationRounded = Math.round((el.rotation || 0) * 10) / 10;

    const stopBubble = (e) => e.stopPropagation();

    /**
     * Helper pour rendre une section "label - / valeur / +" compacte.
     * @param label - String OU element React (pour icone SVG)
     * @param suffix - unite affichee accolee a la valeur dans l'input (ex: '°')
     *
     * Note couleur : on utilise text-white pour le label et le suffix °. Sur les
     * themes clairs, les regles CSS [data-theme$="-light"] .text-white convertissent
     * automatiquement en noir → cohérent avec le thème actif.
     */
    const renderControl = (label, value, step, onUpdate, title, suffix = '') => (
        <div className="flex items-center">
            <span className="font-bold text-[10px] uppercase tracking-widest mr-1.5 select-none text-white flex items-center">
                {label}
            </span>
            <button
                type="button"
                className="w-5 h-5 flex items-center justify-center rounded bg-black/30 hover:bg-forge-mauve/30 text-white/70 hover:text-white transition-colors text-sm font-bold leading-none"
                onClick={() => onUpdate(value - step)}
                title={`Diminuer ${title}`}
                aria-label={`Diminuer ${title}`}
            >−</button>
            <div className="relative flex items-center">
                <input
                    type="number"
                    value={value}
                    step="any"
                    onChange={(e) => onUpdate(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    className={`w-10 h-5 bg-black/40 border-y border-white/10 text-center text-[11px] text-white outline-none focus:border-forge-mauve/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${suffix ? 'pr-2.5' : ''}`}
                />
                {suffix && (
                    <span className="absolute right-0.5 text-[9px] text-white font-mono pointer-events-none select-none">
                        {suffix}
                    </span>
                )}
            </div>
            <button
                type="button"
                className="w-5 h-5 flex items-center justify-center rounded bg-black/30 hover:bg-forge-mauve/30 text-white/70 hover:text-white transition-colors text-sm font-bold leading-none"
                onClick={() => onUpdate(value + step)}
                title={`Augmenter ${title}`}
                aria-label={`Augmenter ${title}`}
            >+</button>
        </div>
    );

    // Icone rotation (fleche circulaire) — plus parlant que la lettre R
    const RotationIcon = (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-9-9c2.4 0 4.7.94 6.4 2.6L21 8" />
            <polyline points="21 3 21 8 16 8" />
        </svg>
    );

    return (
        <div
            data-no-export="true"
            style={hudStyle}
            onMouseDown={stopBubble}
            onClick={stopBubble}
            onDoubleClick={stopBubble}
        >
            <div className="flex items-stretch bg-forge-bg/95 backdrop-blur-md border border-forge-mauve/30 rounded-md shadow-[0_4px_15px_rgba(0,0,0,0.4)] font-mono text-forge-text whitespace-nowrap select-none">
                {/* Poignée de drag à gauche */}
                <div
                    onMouseDown={startDrag}
                    title="Déplacer le panneau"
                    className="flex items-center justify-center px-1 cursor-grab active:cursor-grabbing border-r border-white/10 hover:bg-white/5 rounded-l-md"
                    style={{ touchAction: 'none' }}
                >
                    <svg width="8" height="12" viewBox="0 0 10 14" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white/40">
                        <circle cx="3" cy="3" r="1" fill="currentColor" />
                        <circle cx="7" cy="3" r="1" fill="currentColor" />
                        <circle cx="3" cy="7" r="1" fill="currentColor" />
                        <circle cx="7" cy="7" r="1" fill="currentColor" />
                        <circle cx="3" cy="11" r="1" fill="currentColor" />
                        <circle cx="7" cy="11" r="1" fill="currentColor" />
                    </svg>
                </div>

                {/* Contenu : L, H, R — gaps minimaux entre les sections */}
                <div className="flex items-center gap-1.5 px-1.5 py-1">
                    {renderControl('L', widthRounded, 1, updateW, 'la largeur de 1 mm')}
                    <div className="w-px h-3.5 bg-white/10" />
                    {renderControl('H', heightRounded, 1, updateH, 'la hauteur de 1 mm')}
                    <div className="w-px h-3.5 bg-white/10" />
                    {renderControl(RotationIcon, rotationRounded, 1, updateRotation, 'la rotation de 1°', '°')}
                </div>
            </div>
        </div>
    );
}
